import axios from 'axios';
import config from '../config';

const api = axios.create({
  baseURL: config.apiUrl,
  timeout: 3600000, // 60 minutes for mobile uploads
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add session ID to requests if available
api.interceptors.request.use((config) => {
  const sessionId = localStorage.getItem('sessionId');
  if (sessionId) {
    config.headers['x-session-id'] = sessionId;
  }
  return config;
});

// Session API
export const createSession = async (name) => {
  const response = await api.post('/create-session', { name });
  return response.data;
};

export const getSession = async (sessionId) => {
  const response = await api.get(`/session/${sessionId}`);
  return response.data;
};

// Upload API
export const uploadPhotos = async (sessionId, files, onProgress) => {
  const formData = new FormData();
  formData.append('sessionId', sessionId);
  
  files.forEach((file) => {
    formData.append('photos', file);
  });

  const response = await api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 3600000, // 60 minutes per batch upload (mobile friendly)
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      }
    },
  });

  return response.data;
};

export const listPhotos = async (sessionId) => {
  const response = await api.get(`/list/${sessionId}`);
  return response.data;
};

export const deletePhoto = async (photoId) => {
  const response = await api.delete(`/delete/${photoId}`);
  return response.data;
};

export const updatePhoto = async (photoId, blob) => {
  const formData = new FormData();
  formData.append('photo', blob, 'edited.jpg');

  const response = await api.put(`/update/${photoId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// Sizes API
export const getSizes = async () => {
  const response = await api.get('/sizes');
  return response.data;
};

// Orders API
export const createOrder = async (sessionId, sizeId, quantity) => {
  const response = await api.post('/orders', {
    sessionId,
    sizeId,
    quantity,
  });
  return response.data;
};

export const getOrders = async (sessionId) => {
  const response = await api.get(`/orders/${sessionId}`);
  return response.data;
};

// Admin API
export const adminLogin = async (username, password) => {
  const response = await api.post('/admin/login', { username, password });
  return response.data;
};

export const getAdminStatistics = async () => {
  const response = await api.get('/admin/statistics');
  return response.data;
};

export const getAdminFolders = async (startDate = null, endDate = null) => {
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  
  const response = await api.get('/admin/folders', { params });
  return response.data;
};

export const getAdminFolderDetails = async (sessionId) => {
  const response = await api.get(`/admin/folder/${sessionId}`);
  return response.data;
};

export const downloadFolderZip = async (sessionId) => {
  const response = await api.get(`/admin/download/${sessionId}`, {
    responseType: 'blob',
  });
  return response;
};

export const bulkDeleteFolders = async (sessionIds) => {
  const response = await api.post('/admin/bulk-delete', { sessionIds });
  return response.data;
};

export const getAdminProductTypes = async () => {
  const response = await api.get('/admin/product-types');
  return response.data;
};

export const addProductType = async (name, code, apiParam, description) => {
  const response = await api.post('/admin/product-types', { name, code, apiParam, description });
  return response.data;
};

export const updateProductType = async (id, name, code, apiParam, description, isActive) => {
  const response = await api.put(`/admin/product-types/${id}`, { name, code, apiParam, description, isActive });
  return response.data;
};

export const deleteProductType = async (id) => {
  const response = await api.delete(`/admin/product-types/${id}`);
  return response.data;
};

export const getAdminSizes = async () => {
  const response = await api.get('/admin/sizes');
  return response.data;
};

export const addPrintSize = async (label, productTypeId, displayOrder) => {
  const response = await api.post('/admin/sizes', { label, productTypeId, displayOrder });
  return response.data;
};

export const updatePrintSize = async (id, label, productTypeId, displayOrder, isActive) => {
  const response = await api.put(`/admin/sizes/${id}`, { label, productTypeId, displayOrder, isActive });
  return response.data;
};

export const deletePrintSize = async (id) => {
  const response = await api.delete(`/admin/sizes/${id}`);
  return response.data;
};

export default api;

